import type { NameSearchScope } from '@/services/db/activity-log/entity';
import { LOG_TYPE, objectTypeIdOfLogType, parseLogType } from './activity-log-types';

/** The attribute names an object type has been sent, which seed its pickers before a search. */
export interface NameFilterObjectType {
  id: string;
  sentAttributes: readonly string[] | null | undefined;
  sentRelAttributes: readonly string[] | null | undefined;
}

/** The attribute list an object or relationship attribute-change filter searches, and its seed. */
export interface ObjectAttributePicker {
  scope: Extract<NameSearchScope, { kind: 'object_attribute' | 'relationship_attribute' }>;
  /** Shown before anything is typed. */
  cachedNames: string[];
}

/**
 * What an object type's attribute picker offers before anything has been sent to it: its
 * identifiers and the two attributes every object has (Ember's `names` fallback).
 */
const DEFAULT_OBJECT_ATTRIBUTE_NAMES = ['cio_object_id', 'created_at', 'name', 'object_id'];

const RELATIONSHIP_ATTRIBUTE_TYPES: readonly string[] = [
  LOG_TYPE.RelationshipAttributeChange,
  LOG_TYPE.RelationshipFailedAttributeChange,
];

/** Whether a type filters by the name of an object or relationship attribute. */
export function isObjectAttributeLogType(type: string): boolean {
  const parsedType = parseLogType(type);
  return parsedType === LOG_TYPE.ObjectAttributeChange || RELATIONSHIP_ATTRIBUTE_TYPES.includes(parsedType);
}

/**
 * The attribute list a pinned object or relationship attribute-change type searches: the object
 * type's attributes, or its relationship attributes. Seeded from what that type has been sent,
 * when `objectType` is the one the type names. A wildcard type names no object type and has no
 * list to search, so it has no picker — Ember never runs that search unscoped either, because an
 * unscoped attributes search lists people attributes.
 */
export function objectAttributePicker(
  type: string,
  objectType: NameFilterObjectType | undefined,
): ObjectAttributePicker | null {
  const objectTypeId = objectTypeIdOfLogType(type);
  if (objectTypeId == null) return null;

  const known = objectType?.id === objectTypeId ? objectType : undefined;
  if (RELATIONSHIP_ATTRIBUTE_TYPES.includes(parseLogType(type))) {
    return {
      scope: { kind: 'relationship_attribute', objectTypeId },
      cachedNames: [...(known?.sentRelAttributes ?? [])],
    };
  }
  return {
    scope: { kind: 'object_attribute', objectTypeId },
    cachedNames: [...(known?.sentAttributes ?? DEFAULT_OBJECT_ATTRIBUTE_NAMES)],
  };
}
